<section class="hero wave-bg text-[var(--text)]">
  <div class="container hero-content hero-content--centered">
    <h1 class="text-gray-900 dark:text-white">
      Intégration IA
      <span class="text-[#4cc9f0]">& Solutions LLM sur Mesure</span>
    </h1>
    <p class="hero-description !text-gray-700 dark:!text-gray-300">
      L'<strong>intégration IA</strong> est le levier de compétitivité décisif pour 2026. Mesh Box connecte vos données métiers aux modèles de langage les plus performants (Azure OpenAI, Mistral, Llama) de manière sécurisée. Transformez votre SI existant en véritable moteur de croissance.
    </p>
    <div class="cta-row">
      <a 
        href="https://cal.mesh-box.fr/ogirodin/30min"
        class="button primary large bg-[#4cc9f0] text-[#1a1a2e] hover:bg-[#3db5d8] transition-colors"
        data-cal-link="ogirodin/30min" 
        data-cal-config='{"layout":"month_view"}'
      >
        Discuter de votre projet IA
      </a>
    </div>
    <div class="mt-6 flex justify-center">
      <app-cii-badge variant="inline" />
    </div>
  </div>
</section>

<section class="section">
  <div class="container">
    <app-cii-badge variant="table" />
  </div>
</section>

<section class="section alt">
  <div class="container">
    <h2 class="text-gray-900 dark:text-white">Au-delà du Chatbot : L'IA Opérationnelle & ROI</h2>
    <p class="text-gray-700 dark:text-gray-300">
      Une <strong>intégration IA</strong> réussie en entreprise ne se limite pas à un simple agent conversationnel. Il s'agit de déployer une intelligence opérationnelle au cœur de vos processus critiques.
    </p>
    <p class="mt-4 text-gray-700 dark:text-gray-300">
      Les études récentes (2025-2026) démontrent un impact majeur : une réduction de <strong>30 à 50% du temps consacré aux tâches cognitives répétitives</strong>.
    </p>
    <div class="grid-3 mt-8">
      <div class="card border-l-4 border-[#4cc9f0]">
        <h4 class="text-gray-900 dark:text-white">Support Client Augmenté</h4>
        <p class="text-gray-700 dark:text-gray-300">Résolution de +40% des tickets niveau 1 sans intervention humaine grâce au RAG sur documentation technique.</p>
      </div>
      <div class="card border-l-4 border-[#4cc9f0]">
        <h4 class="text-gray-900 dark:text-white">Analyse Juridique & RH</h4>
        <p class="text-gray-700 dark:text-gray-300">Screening de CV (-70% de temps) ou pré-analyse de contrats pour surligner les clauses à risque.</p>
      </div>
      <div class="card border-l-4 border-[#4cc9f0]">
        <h4 class="text-gray-900 dark:text-white">Optimisation Logistique</h4>
        <p class="text-gray-700 dark:text-gray-300">À l'image du secteur aérien économisant le carburant, nos algorithmes optimisent vos flux de données complexes.</p>
      </div>
    </div>
  </div>
</section>

<section class="section">
  <div class="container">
    <h2 class="text-gray-900 dark:text-white">Réussir votre projet d'intégration IA</h2>
    <p class="text-center max-w-4xl mx-auto mb-12 text-gray-700 dark:text-gray-300">
      Nous déployons des architectures robustes (RAG, Agents) adaptées à la reality de votre dette technique et de vos exigences de sécurité.
    </p>
    <div class="grid-3">
      <div class="card">
        <div class="text-5xl mb-4">🔍</div>
        <h3 class="text-gray-900 dark:text-white">RAG Entreprise (Retrieval-Augmented Generation)</h3>
        <p class="text-gray-700 dark:text-gray-300">Connectez l'IA à vos documents internes (PDF, SharePoint, Confluence, SQL). Une <strong>intégration IA</strong> qui permet d'interroger votre base de connaissances en langage naturel, avec citation des sources pour éviter les hallucinations.</p>
      </div>
      <div class="card">
        <div class="text-5xl mb-4">🤖</div>
        <h3 class="text-gray-900 dark:text-white">Agents IA Métier & Autonomes</h3>
        <p class="text-gray-700 dark:text-gray-300">Déployez des agents capables d'actions concrètes (Function Calling) : interrogation d'API tierces, création de tickets Jira, ou génération de rapports d'audit automatisés directement dans votre ERP.</p>
      </div>
      <div class="card">
        <div class="text-5xl mb-4">⚙️</div>
        <h3 class="text-gray-900 dark:text-white">Modernisation Legacy Assistée</h3>
        <p class="text-gray-700 dark:text-gray-300">Utilisez l'IA pour documenter, tester et refactoriser votre code legacy (Node.js, Angular, Java). Une stratégie d'intégration IA technique pour réduire votre dette technique et sécuriser vos mises à jour.</p>
      </div>
    </div>
    <div class="text-center mt-12">
        <a href="https://cal.mesh-box.fr/ogirodin/30min" class="button primary" data-cal-link="ogirodin/30min">Lancer votre audit IA</a>
    </div>
  </div>
</section>

<section class="section alt">
  <div class="container">
    <h2 class="text-gray-900 dark:text-white">Les 8 Piliers d'une Intégration IA Réussie</h2>
    <p class="mb-8 text-gray-700 dark:text-gray-300">Basé sur les meilleures pratiques 2026 pour les SI d'entreprise.</p>
    
    <div class="grid-2">
      <div>
        <ol class="list-none pl-0 [counter-reset:item]">
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">1</span>
            <strong>Évaluer la maturité des données :</strong> Pas d'IA sans données propres. Nous auditons vos sources (Data Quality).
          </li>
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">2</span>
            <strong>Équipe Pluridisciplinaire :</strong> Implication des métiers, de l'IT et du légal (DPO) dès le jour 1.
          </li>
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">3</span>
            <strong>Alignement Business :</strong> Définir des KPIs clairs (temps gagné, taux d'erreur réduit).
          </li>
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">4</span>
            <strong>Viser les "Quick Wins" :</strong> Commencer par un POC (Proof of Concept) à fort impact et faible risque.
          </li>
        </ol>
      </div>
      <div>
        <ol class="list-none pl-0 [counter-reset:item_4]">
           <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">5</span>
            <strong>Modélisation du ROI :</strong> Calculer le coût des tokens vs le coût humain économisé.
          </li>
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">6</span>
            <strong>Développement Agile :</strong> Itérations rapides pour ajuster les prompts et le contexte (Prompt Engineering).
          </li>
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">7</span>
            <strong>Scalabilité & MLOps :</strong> Prévoir le passage à l'échelle et la surveillance des modèles.
          </li>
          <li class="mb-4 relative pl-10 text-gray-700 dark:text-gray-300">
            <span class="absolute left-0 top-0 bg-[#4cc9f0] text-[#1a1a2e] w-[1.8rem] h-[1.8rem] rounded-full flex items-center justify-center font-bold leading-[1.8rem]">8</span>
            <strong>Maintenance Continue :</strong> Mise à jour des bases de connaissances et ré-entraînement fin (Fine-tuning).
          </li>
        </ol>
      </div>
    </div>

    <h3 class="mt-12 mb-6 text-gray-900 dark:text-white">Comparatif des modèles pour votre intégration</h3>
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-[var(--border)] bg-[var(--card)] rounded-lg overflow-hidden shadow-lg border border-[var(--border)]">
        <thead class="bg-[var(--panel)]">
          <tr>
            <th scope="col" class="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">Modèle / Plateforme</th>
            <th scope="col" class="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">Type</th>
            <th scope="col" class="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">Sécurité & Souveraineté</th>
            <th scope="col" class="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider text-[var(--muted)]">Idéal pour</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-[var(--border)]">
          <tr class="hover:bg-[var(--brand)]/5 transition-colors duration-200">
            <td class="px-6 py-5 whitespace-nowrap text-sm font-bold text-[var(--text)]">Azure OpenAI</td>
            <td class="px-6 py-5 whitespace-nowrap text-sm text-[var(--text)]">SaaS (Cloud)</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">Instance privée, conformité Enterprise, ISO 27001</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">Entreprises déjà sous écosystème Microsoft 365.</td>
          </tr>
          <tr class="hover:bg-[var(--brand)]/5 transition-colors duration-200">
            <td class="px-6 py-5 whitespace-nowrap text-sm font-bold text-[var(--text)]">AWS Bedrock</td>
            <td class="px-6 py-5 whitespace-nowrap text-sm text-[var(--text)]">SaaS (Multi-modèles)</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">Isolation VPC, Chiffrement KMS</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">Besoin de flexibilité (Claude, Titan, Llama) et scalabilité AWS.</td>
          </tr>
          <tr class="hover:bg-[var(--brand)]/5 transition-colors duration-200">
            <td class="px-6 py-5 whitespace-nowrap text-sm font-bold text-[var(--text)]">Mistral AI</td>
            <td class="px-6 py-5 whitespace-nowrap text-sm text-[var(--text)]">SaaS ou Open Weight</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">🇪🇺 Hébergement France/Europe, Souveraineté</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">Souveraineté des données et performance/coût.</td>
          </tr>
          <tr class="hover:bg-[var(--brand)]/5 transition-colors duration-200">
            <td class="px-6 py-5 whitespace-nowrap text-sm font-bold text-[var(--text)]">Llama (On-Premise)</td>
            <td class="px-6 py-5 whitespace-nowrap text-sm text-[var(--text)]">Open Source</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">🏠 Contrôle total, Zéro fuite cloud (Air-gapped possible)</td>
            <td class="px-6 py-5 text-sm text-[var(--text-light)]">Données ultra-sensibles, secret industriel, défense.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</section>

 <section class="section bg-gray-100 dark:bg-[#1a1a2e] text-gray-900 dark:text-white">
  <div class="container">
    <h2 class="text-gray-900 dark:text-white">Intégration IA Sécurisée & Souveraine (RGPD)</h2>
    <div class="grid-2 items-center text-left">
      <div>
         <p class="text-lg mb-6 text-gray-700 dark:text-gray-300">
           La conformité CNIL et RGPD est au cœur de notre démarche d'<strong>intégration IA</strong>. Nous appliquons le principe de minimisation des données.
         </p>
         <ul class="list-none p-0">
           <li class="mb-4 flex items-start">
             <span class="text-[#4cc9f0] mr-2">🛡️</span>
             <span class="text-gray-800 dark:text-gray-200"><strong>Pas d'entraînement public :</strong> Vos données ne servent jamais à entraîner les modèles publics (ChatGPT, etc.).</span>
           </li>
           <li class="mb-4 flex items-start">
             <span class="text-[#4cc9f0] mr-2">🇪🇺</span>
             <span class="text-gray-800 dark:text-gray-200"><strong>Hébergement France/UE :</strong> Nous privilégions des zones de disponibilité européennes pour garantir la souveraineté.</span>
           </li>
           <li class="mb-4 flex items-start">
             <span class="text-[#4cc9f0] mr-2">🏠</span>
             <span class="text-gray-800 dark:text-gray-200"><strong>Mode On-Premise :</strong> Pour les données critiques, nous installons les modèles (LLM) directement sur vos serveurs via Docker/Kubernetes.</span>
           </li>
           <li class="mb-4 flex items-start">
             <span class="text-[#eac639] mr-2">🏛️</span>
             <span class="text-gray-800 dark:text-gray-200"><strong>Agréé Crédit Impôt Innovation :</strong> Vos projets d'IA avec Mesh Box sont éligibles à 20% de crédit d'impôt — Agrément CII n°29987181 (2026–2030).</span>
           </li>
         </ul>
      </div>
      <div class="card bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 backdrop-blur-md">
         <h4 class="text-gray-900 dark:text-white">Cas Client : Cabinet d'Audit Financier</h4>
         <p class="text-sm text-gray-600 dark:text-gray-300 italic mb-4">
           "Nous avions besoin d'automatiser l'analyse de bilans sans que les données ne quittent nos serveurs."
         </p>
         <p class="text-sm text-gray-800 dark:text-white">
           <strong>Solution Mesh Box :</strong> Déploiement d'un modèle Llama 3 affiné, hébergé sur une instance GPU privée. Résultat : analyse des liasses fiscales en 30s vs 45min, zéro donnée exposée sur le cloud.
         </p>
      </div>
    </div>
    <div class="text-center mt-8">
       <a href="https://cal.mesh-box.fr/ogirodin/30min" class="button primary bg-[#4cc9f0] text-[#1a1a2e] hover:bg-[#3db5d8]" data-cal-link="ogirodin/30min">Sécuriser vos données IA</a>
    </div>
  </div>
</section>

<section class="section">
  <div class="container">
    <h2>Questions fréquentes sur l’intégration IA</h2>
    <div class="max-w-4xl mx-auto text-left">
      
      <div class="mb-8">
        <h3 class="text-xl mb-2 font-bold text-gray-900 dark:text-white">Quelles sont les étapes clés d'un projet d'intégration IA ?</h3>
        <p class="text-gray-700 dark:text-gray-300">
          Une intégration réussie suit 3 phases majeures : 1. <strong>Audit & Cadrage</strong> (qualification des données, choix du modèle, sécurité), 2. <strong>Développement du POC</strong> (Proof of Concept sur un périmètre restreint pour valider le ROI), et 3. <strong>Industrialisation</strong> (mise à l'échelle, intégration CI/CD, monitoring MLOps).
        </p>
      </div>

      <div class="mb-8">
        <h3 class="text-xl mb-2 font-bold text-gray-900 dark:text-white">Quels sont les prérequis techniques pour une intégration IA en entreprise ?</h3>
        <p class="text-gray-700 dark:text-gray-300">
          Le prérequis n°1 est l'accessibilité et la qualité de la donnée. Vos documents doivent être numérisés (OCR si besoin) et vos bases de données accessibles via API sécurisées. Mesh Box s'occupe de la couche d'ingestion (Vector Database) nécessaire au RAG.
        </p>
      </div>

      <div class="mb-8">
        <h3 class="text-xl mb-2 font-bold text-gray-900 dark:text-white">Comment choisir entre modèle Cloud (Azure/OpenAI) et On-Premise (Llama) ?</h3>
        <p class="text-gray-700 dark:text-gray-300">
          Ce choix dépend de votre politique de souveraineté. Pour une mise en place rapide et performante, le Cloud (Azure OpenAI Enterprise) est idéal. Pour une confidentialité absolue (Secret Défense, Santé, Brevets), l'<strong>intégration IA On-Premise</strong> avec des modèles Open Weights (Mistral, Llama) est recommandée malgré un coût d'infrastructure initial plus élevé.
        </p>
      </div>

      <div class="mb-8">
        <h3 class="text-xl mb-2 font-bold text-gray-900 dark:text-white">Comment garantir la sécurité des données avec l’IA ?</h3>
        <p class="text-gray-700 dark:text-gray-300">
          Nous utilisons des techniques de "Sanitization" pour anonymiser les données personnelles avant envoi au modèle (PII Scrubbing). De plus, les contrats "Enterprise" des fournisseurs (Microsoft, AWS) garantissent contractuellement que vos données ne servent pas à l'entraînement de leurs modèles globaux.
        </p>
      </div>

      <div class="mb-8">
        <h3 class="text-xl mb-2 font-bold text-gray-900 dark:text-white">Quel est le ROI d’une intégration IA pour un SI existant ?</h3>
        <p class="text-gray-700 dark:text-gray-300">
          Le ROI se mesure sur la productivité et la qualité. Nos clients observent un ROI positif en 6 à 9 mois grâce à la réduction des tâches chronophages (saisie, recherche) et l'amélioration de la satisfaction client (réactivité).
        </p>
      </div>
      
       <div class="mb-8">
        <h3 class="text-xl mb-2 font-bold text-gray-900 dark:text-white">Avez-vous des exemples concrets de cas d'usage RAG ?</h3>
        <p class="text-gray-700 dark:text-gray-300">
          Oui. Pour un industriel, nous avons indexé 50 000 fiches techniques : les techniciens terrain interrogent l'IA sur tablette pour obtenir la procédure de maintenance exacte en 3 secondes. Pour un service juridique, l'IA prépare des synthèses de conformité contractuelle automatiquement.
        </p>
      </div>

    </div>
  </div>
</section>

<section class="section cta-final">
  <div class="container">
    <h2>Prêt à réussir votre intégration IA ?</h2>
    <p>Ne laissez pas la concurrence prendre de l'avance technologique. Échangeons sur vos besoins spécifiques lors d'un audit flash de 30 minutes offert.</p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
      <a
        href="https://cal.mesh-box.fr/ogirodin/30min"
        class="button primary large"
        data-cal-link="ogirodin/30min"
      >
        Réserver mon créneau
      </a>
      <a href="/simulateur-cii" class="button large outline">
        🏛️ Calculer mon crédit d'impôt →
      </a>
    </div>
  </div>
</section>
